import React from 'react';
import { Field, NumberInput, TextInput, SelectInput, ArrayEditor, FinancialGrid, PrimaryButton, NextButton, GhostButton, Badge } from './dprFields';
import { OS_LINES, BS_LINES } from './dprCmaLines';
import { BUSINESS_FORMATION_OPTIONS } from './CaseWizard';

export const CMA_STEP_GROUPS = [
  { group: 'Setup', steps: [
    { id: 'company', label: 'Company & Bank Details' },
    { id: 'years', label: 'Years Setup' },
  ]},
  { group: 'Financial Data', steps: [
    { id: 'os', label: '1. Operating Statement' },
    { id: 'bs', label: '2. Balance Sheet' },
  ]},
  { group: 'Financing', steps: [
    { id: 'term_loans', label: 'TL Schedule' },
    { id: 'wc_method', label: 'Working Capital Method' },
  ]},
];
export const CMA_ALL_STEPS = CMA_STEP_GROUPS.flatMap(g => g.steps);

function StepFooter({ stepIndex, onBack, onSave, onNext, isLastStep, nextLabel, saving }) {
  const label = nextLabel || (isLastStep ? 'Generate Report' : 'Next');
  return (
    <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100 dark:border-slate-700">
      <GhostButton onClick={onBack} className={stepIndex === 0 ? 'invisible' : ''}>← Back</GhostButton>
      <div className="flex items-center gap-3">
        <PrimaryButton onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
        <NextButton onClick={onNext} disabled={saving}>{label}</NextButton>
      </div>
    </div>
  );
}
function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-5">
      <h3 className="text-base font-extrabold text-slate-800 dark:text-slate-100">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function buildYears(actualCount, estimatedCount, projectedCount, existing) {
  const years = [];
  const prevLabels = (existing || []).map(y => y.label);
  let li = 0;
  for (let i = 0; i < actualCount; i++) { years.push({ label: prevLabels[li] || `FY (Actual ${i + 1})`, type: 'actual' }); li++; }
  for (let i = 0; i < estimatedCount; i++) { years.push({ label: prevLabels[li] || `FY (Estimated ${i + 1})`, type: 'estimated' }); li++; }
  for (let i = 0; i < projectedCount; i++) { years.push({ label: prevLabels[li] || `FY (Projected ${i + 1})`, type: 'projected' }); li++; }
  return years;
}

export default function CmaWizard({ caseData, setCaseData, stepId, onNext, onBack, onSave, saving, isLastStep }) {
  const set = (field, value) => setCaseData(c => ({ ...c, [field]: value }));
  const setNested = (field, key, value) => setCaseData(c => ({ ...c, [field]: { ...(c[field] || {}), [key]: value } }));

  const cp = caseData.company_profile || {};
  const ys = caseData.years_setup || {};
  const years = ys.years || [];
  const df = caseData.direct_financials || {};
  const wc = caseData.wc_config || {};
  const termLoans = caseData.term_loans || [];

  const stepIndex = CMA_ALL_STEPS.findIndex(s => s.id === stepId);

  const setGridValue = (section, key, yearIdx, val) => {
    const grid = { ...(df[section] || {}) };
    const row = (grid[key] || []).slice();
    row[yearIdx] = val;
    grid[key] = row;
    set('direct_financials', { ...df, [section]: grid });
  };
  const setCustomLine = (section, idx, field, val) => {
    const list = ((df[`${section}CustomLines`] || [])).slice();
    list[idx] = { ...list[idx], [field]: val };
    set('direct_financials', { ...df, [`${section}CustomLines`]: list });
  };
  const addCustomLine = (section) => {
    const list = [...(df[`${section}CustomLines`] || []), { label: '', values: years.map(() => 0) }];
    set('direct_financials', { ...df, [`${section}CustomLines`]: list });
  };
  const removeCustomLine = (section, idx) => {
    set('direct_financials', { ...df, [`${section}CustomLines`]: (df[`${section}CustomLines`] || []).filter((_, i) => i !== idx) });
  };

  const bsTotals = years.length ? (() => {
    const capital = df.bs?.capital || [], reserves = df.bs?.reserves || [], tl = df.bs?.termLoanOutstanding || [];
    const wcb = df.bs?.wcBorrowing || [], cr = df.bs?.sundryCreditors || [], ocl = df.bs?.otherCurrentLiabilities || [];
    const land = df.bs?.freeholdLand || [], nb = df.bs?.netBlock || [], cash = df.bs?.cashAndBank || [];
    const rec = df.bs?.receivables || [], inv = df.bs?.inventory || [], oca = df.bs?.otherCurrentAssets || [];
    return years.map((_, i) => {
      const liab = (Number(capital[i]) || 0) + (Number(reserves[i]) || 0) + (Number(tl[i]) || 0) + (Number(wcb[i]) || 0) + (Number(cr[i]) || 0) + (Number(ocl[i]) || 0);
      const assets = (Number(land[i]) || 0) + (Number(nb[i]) || 0) + (Number(cash[i]) || 0) + (Number(rec[i]) || 0) + (Number(inv[i]) || 0) + (Number(oca[i]) || 0);
      return Math.round((assets - liab) * 100) / 100;
    });
  })() : [];

  return (
    <div className="max-w-5xl">
      {stepId === 'company' && (
        <div>
          <StepHeader title="Company & Bank Details" subtitle="Borrower and bank/branch details for the CMA cover sheet" />
          <div className="grid grid-cols-2 gap-x-5 max-w-3xl">
            <Field label="Business / Company Name" required><TextInput value={caseData.applicant_name} onChange={v => set('applicant_name', v)} /></Field>
            <Field label="Constitution">
              <SelectInput value={cp.businessFormation} onChange={v => setNested('company_profile', 'businessFormation', v)} options={BUSINESS_FORMATION_OPTIONS} />
            </Field>
            {cp.businessFormation === 'Others' && (
              <Field label="Specify Constitution"><TextInput value={cp.businessFormationOther} onChange={v => setNested('company_profile', 'businessFormationOther', v)} placeholder="e.g. Cooperative Society" /></Field>
            )}
            <Field label="Registered Address"><TextInput value={cp.address} onChange={v => setNested('company_profile', 'address', v)} /></Field>
            <Field label="Nature of Activity"><TextInput value={cp.natureOfActivity} onChange={v => setNested('company_profile', 'natureOfActivity', v)} /></Field>
            <Field label="Bank Name"><TextInput value={cp.bankName} onChange={v => setNested('company_profile', 'bankName', v)} /></Field>
            <Field label="Branch"><TextInput value={cp.branchName} onChange={v => setNested('company_profile', 'branchName', v)} /></Field>
            <Field label="Existing CC / WC Limit"><NumberInput value={cp.existingCCLimit} onChange={v => setNested('company_profile', 'existingCCLimit', v)} /></Field>
            <Field label="Proposed CC / WC Limit"><NumberInput value={cp.proposedCCLimit} onChange={v => setNested('company_profile', 'proposedCCLimit', v)} /></Field>
          </div>
          <Field label="Remarks"><TextInput value={cp.remarks} onChange={v => setNested('company_profile', 'remarks', v)} /></Field>
          <StepFooter stepIndex={stepIndex} onBack={onBack} onSave={onSave} onNext={onNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'years' && (
        <div>
          <StepHeader title="Years Setup" subtitle="Actual = audited past years · Estimated = current year · Projected = future years being proposed" />
          <div className="grid grid-cols-3 gap-4 max-w-xl mb-4">
            <Field label="Actual Years"><NumberInput value={ys.actualCount ?? 2} onChange={v => setNested('years_setup', 'actualCount', v)} /></Field>
            <Field label="Estimated Years"><NumberInput value={ys.estimatedCount ?? 1} onChange={v => setNested('years_setup', 'estimatedCount', v)} /></Field>
            <Field label="Projected Years"><NumberInput value={ys.projectedCount ?? 3} onChange={v => setNested('years_setup', 'projectedCount', v)} /></Field>
          </div>
          <GhostButton onClick={() => setNested('years_setup', 'years', buildYears(ys.actualCount ?? 2, ys.estimatedCount ?? 1, ys.projectedCount ?? 3, ys.years))}>
            Apply
          </GhostButton>

          {years.length > 0 && (
            <div className="mt-5 grid grid-cols-3 gap-3 max-w-3xl">
              {years.map((y, i) => (
                <div key={i} className="p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                  <Badge tone={y.type === 'actual' ? 'slate' : 'indigo'}>{y.type}</Badge>
                  <div className="mt-2">
                    <TextInput value={y.label} onChange={v => {
                      const next = years.slice(); next[i] = { ...next[i], label: v };
                      setNested('years_setup', 'years', next);
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <StepFooter stepIndex={stepIndex} onBack={onBack} onSave={onSave} onNext={onNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'os' && (
        <div>
          <StepHeader title="1. Operating Statement" subtitle="Type figures for each year — white columns are Actual, blue are Estimated/Projected" />
          {years.length === 0 ? <p className="text-sm text-amber-600">Set up your years first (Years Setup step).</p> : (
            <FinancialGrid years={years} lines={OS_LINES} values={df.os || {}} customLines={df.osCustomLines || []}
              onChangeValue={(k, i, v) => setGridValue('os', k, i, v)}
              onChangeCustom={(i, f, v) => setCustomLine('os', i, f, v)}
              onAddCustom={() => addCustomLine('os')} onRemoveCustom={i => removeCustomLine('os', i)} />
          )}
          <StepFooter stepIndex={stepIndex} onBack={onBack} onSave={onSave} onNext={onNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'bs' && (
        <div>
          <StepHeader title="2. Balance Sheet" subtitle="Must balance (Assets − Liabilities = 0) for every year before generating the report" />
          {years.length === 0 ? <p className="text-sm text-amber-600">Set up your years first (Years Setup step).</p> : (
            <FinancialGrid years={years} lines={BS_LINES} values={df.bs || {}} customLines={df.bsCustomLines || []}
              onChangeValue={(k, i, v) => setGridValue('bs', k, i, v)}
              onChangeCustom={(i, f, v) => setCustomLine('bs', i, f, v)}
              onAddCustom={() => addCustomLine('bs')} onRemoveCustom={i => removeCustomLine('bs', i)}
              totals={{ label: 'Balance Check (Assets − Liabilities)', values: bsTotals }} />
          )}
          <StepFooter stepIndex={stepIndex} onBack={onBack} onSave={onSave} onNext={onNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'term_loans' && (
        <div>
          <StepHeader title="Term Loan Schedule" subtitle="Add every existing and proposed term loan — DSCR is computed on the combined debt service" />
          <ArrayEditor rows={termLoans} columns={[
            { key: 'name', label: 'Loan' }, { key: 'amount', label: 'Amount', type: 'number' },
            { key: 'ratePct', label: 'Rate % p.a.', type: 'number' }, { key: 'tenorMonths', label: 'Tenor (months)', type: 'number' },
            { key: 'moratoriumMonths', label: 'Moratorium (months)', type: 'number' },
          ]} onChange={rows => set('term_loans', rows)} addLabel="+ Add Term Loan" />
          <StepFooter stepIndex={stepIndex} onBack={onBack} onSave={onSave} onNext={onNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'wc_method' && (
        <div>
          <StepHeader title="Working Capital Assessment Method" />
          <Field label="Method">
            <SelectInput value={wc.method || 'traditional'} onChange={v => setNested('wc_config', 'method', v)} options={[
              { value: 'traditional', label: 'Traditional Method' }, { value: 'turnover', label: 'Turnover Method' },
              { value: 'mpbf2', label: 'MPBF — 2nd Method of Lending (Tandon)' }, { value: 'mpbf3', label: 'MPBF — 3rd Method of Lending (Tandon)' },
            ]} />
          </Field>
          {wc.method === 'traditional' && (
            <div className="grid grid-cols-2 gap-x-5 max-w-xl">
              <Field label="Stock Margin % (promoter)" hint="default 25"><NumberInput value={(wc.traditional || {}).stockMarginPct} onChange={v => set('wc_config', { ...wc, traditional: { ...(wc.traditional || {}), stockMarginPct: v } })} /></Field>
              <Field label="Debtor Margin % (promoter)" hint="default 40"><NumberInput value={(wc.traditional || {}).debtorMarginPct} onChange={v => set('wc_config', { ...wc, traditional: { ...(wc.traditional || {}), debtorMarginPct: v } })} /></Field>
            </div>
          )}
          {(wc.method === 'mpbf2' || wc.method === 'mpbf3') && (
            <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 mt-1 max-w-xl">
              MPBF (Tandon Committee, Method 2/3) — validate against your bank's actual sanction letter.
            </p>
          )}
          <StepFooter stepIndex={stepIndex} onBack={onBack} onSave={onSave} onNext={onNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}
    </div>
  );
}
